/**
 * Intent Classifier Service
 * Classifies user queries into navigate/guide/agent modes
 * Tries local pattern matching first, falls back to AI classification
 */

import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClassifiedIntent } from '../models/app-knowledge.models';
import { AppKnowledgeService } from './app-knowledge.service';
import { AiAssistApiService } from '../services/ai-assist-api.service';

@Injectable({ providedIn: 'root' })
export class IntentClassifierService {

  // ============================================================
  // PATTERN MATCHING RULES
  // ============================================================

  private readonly NAVIGATE_PATTERNS = [
    /^(go to|take me to|open|show me|navigate to)\s+/i,
    /^(where is|find)\s+/i,
    /^show\s+/i,
  ];

  private readonly NAVIGATE_KEYWORDS = [
    'dashboard', 'contacts', 'deals', 'reports', 'settings',
    'conversations', 'metrics', 'analytics', 'profile', 'calendar'
  ];

  private readonly GUIDE_PATTERNS = [
    /^(how do i|how to|how can i)\s+/i,
    /^(help me|show me how|teach me)\s+/i,
    /^(walk me through|guide me)\s+/i,
    /\?$/  // Questions are usually guide requests
  ];

  private readonly GUIDE_KEYWORDS = [
    'import', 'export', 'create', 'add', 'upload', 'download',
    'send', 'make', 'generate', 'build', 'setup', 'configure'
  ];

  private readonly AGENT_PATTERNS = [
    /^(create|add|delete|update|send|schedule)\s+/i,
    /^(import|export|upload|download)\s+/i,
    /^(set|change|modify)\s+/i,
  ];

  private readonly AGENT_INDICATORS = [
    'for me', 'automatically', 'bulk', 'all', 'multiple',
    'every', 'from file', 'csv', 'excel'
  ];

  constructor(
    private knowledgeService: AppKnowledgeService,
    private apiService: AiAssistApiService
  ) {}

  // ============================================================
  // CLASSIFICATION METHODS
  // ============================================================

  /**
   * Classify user query into navigate/guide/agent mode
   * Returns best classification with confidence score
   */
  async classify(query: string): Promise<ClassifiedIntent> {
    const queryLower = query.toLowerCase().trim();

    // Try local classification first (fast)
    const localResult = this.classifyLocal(queryLower);
    if (localResult.confidence > 0.7) {
      return localResult;
    }

    // Fall back to AI classification (slower but more accurate)
    try {
      const aiResult = await this.classifyWithAI(query);
      // Use AI result if confident, otherwise use local
      return aiResult.confidence > localResult.confidence ? aiResult : localResult;
    } catch (error) {
      console.warn('AI classification failed, using local result:', error);
      return localResult;
    }
  }

  /**
   * Fast local classification using pattern matching
   */
  private classifyLocal(query: string): ClassifiedIntent {
    const scores = {
      navigate: 0,
      guide: 0,
      agent: 0
    };

    // Check navigate patterns
    for (const pattern of this.NAVIGATE_PATTERNS) {
      if (pattern.test(query)) {
        scores.navigate += 20;
      }
    }

    // Check navigate keywords
    for (const keyword of this.NAVIGATE_KEYWORDS) {
      if (query.includes(keyword)) {
        scores.navigate += 10;
      }
    }

    // Check guide patterns
    for (const pattern of this.GUIDE_PATTERNS) {
      if (pattern.test(query)) {
        scores.guide += 20;
      }
    }

    // Check guide keywords
    for (const keyword of this.GUIDE_KEYWORDS) {
      if (query.includes(keyword)) {
        scores.guide += 10;
      }
    }

    // Check agent patterns
    for (const pattern of this.AGENT_PATTERNS) {
      if (pattern.test(query)) {
        scores.agent += 15;
      }
    }

    // Check agent indicators
    for (const indicator of this.AGENT_INDICATORS) {
      if (query.includes(indicator)) {
        scores.agent += 10;
      }
    }

    // Additional heuristics
    if (query.includes('?')) {
      scores.guide += 5;  // Questions are usually guide requests
    }

    if (query.split(' ').length > 10) {
      scores.agent += 5;  // Long queries often need agent mode
    }

    // Determine best mode
    const maxScore = Math.max(scores.navigate, scores.guide, scores.agent);
    const totalScore = scores.navigate + scores.guide + scores.agent;
    const confidence = totalScore > 0 ? maxScore / totalScore : 0;

    let mode: 'navigate' | 'guide' | 'agent';
    if (scores.navigate === maxScore) {
      mode = 'navigate';
    } else if (scores.guide === maxScore) {
      mode = 'guide';
    } else {
      mode = 'agent';
    }

    // Build result based on mode
    const result: ClassifiedIntent = {
      mode,
      confidence: Math.min(confidence, 0.9) // Cap at 0.9 for local classification
    };

    // Add mode-specific routing
    if (mode === 'navigate') {
      const routes = this.knowledgeService.searchRoutes(query);
      if (routes.length > 0) {
        result.targetRoute = routes[0].path;
        result.confidence = Math.min(result.confidence + 0.1, 0.95);
      }
    } else if (mode === 'guide') {
      // Search for matching workflow via actions
      const actions = this.knowledgeService.searchActions(query);
      if (actions.length > 0 && actions[0].route) {
        result.targetRoute = actions[0].route;
        if (actions[0].workflowId) {
          result.workflowId = actions[0].workflowId;
        }
        result.confidence = Math.min(result.confidence + 0.1, 0.95);
      }

      // No fuzzy fallback — only use explicitly matched workflows
    } else if (mode === 'agent') {
      // Find matching action definition
      const actions = this.knowledgeService.searchActions(query);
      if (actions.length > 0) {
        result.actionId = actions[0].id;
        result.confidence = Math.min(result.confidence + 0.1, 0.95);
      }
    }

    return result;
  }

  /**
   * AI-powered classification using backend
   */
  private async classifyWithAI(query: string): Promise<ClassifiedIntent> {
    try {
      const response = await firstValueFrom(
        this.apiService.classifyIntent(query)
      );
      return response;
    } catch (error) {
      // Return low-confidence fallback on error
      return {
        mode: 'guide',
        confidence: 0.3
      };
    }
  }

  // ============================================================
  // CONVENIENCE METHODS
  // ============================================================

  /**
   * Check if query is likely a navigation request
   */
  async isNavigationQuery(query: string): Promise<boolean> {
    const result = await this.classify(query);
    return result.mode === 'navigate' && result.confidence > 0.5;
  }

  /**
   * Check if query is likely a guide request
   */
  async isGuideQuery(query: string): Promise<boolean> {
    const result = await this.classify(query);
    return result.mode === 'guide' && result.confidence > 0.5;
  }

  /**
   * Check if query is likely an agent task request
   */
  async isAgentQuery(query: string): Promise<boolean> {
    const result = await this.classify(query);
    return result.mode === 'agent' && result.confidence > 0.5;
  }

  /**
   * Get suggested mode for a query (for debugging/testing)
   */
  async getSuggestedMode(query: string): Promise<{
    mode: 'navigate' | 'guide' | 'agent';
    confidence: number;
    reasoning: string;
  }> {
    const result = await this.classify(query);

    let reasoning = '';
    if (result.mode === 'navigate') {
      reasoning = `Query appears to be a navigation request`;
      if (result.targetRoute) {
        reasoning += ` to ${result.targetRoute}`;
      }
    } else if (result.mode === 'guide') {
      reasoning = `Query appears to be a how-to question requiring step-by-step guidance`;
    } else {
      reasoning = `Query appears to be a task that could be automated`;
      if (result.actionId) {
        reasoning += ` using action ${result.actionId}`;
      }
    }

    return {
      mode: result.mode,
      confidence: result.confidence,
      reasoning
    };
  }

  // ============================================================
  // BATCH CLASSIFICATION (for analytics/testing)
  // ============================================================

  /**
   * Classify multiple queries at once (for testing/analytics)
   */
  async classifyBatch(queries: string[]): Promise<ClassifiedIntent[]> {
    const results: ClassifiedIntent[] = [];

    for (const query of queries) {
      try {
        const result = await this.classify(query);
        results.push(result);
      } catch (error) {
        console.error(`Failed to classify query: "${query}"`, error);
        results.push({
          mode: 'guide',
          confidence: 0
        });
      }
    }

    return results;
  }

  /**
   * Get classification statistics for a set of queries
   */
  async getClassificationStats(queries: string[]): Promise<{
    navigate: number;
    guide: number;
    agent: number;
    avgConfidence: number;
  }> {
    const results = await this.classifyBatch(queries);

    const stats = {
      navigate: 0,
      guide: 0,
      agent: 0,
      avgConfidence: 0
    };

    for (const result of results) {
      stats[result.mode]++;
      stats.avgConfidence += result.confidence;
    }

    stats.avgConfidence = results.length > 0
      ? stats.avgConfidence / results.length
      : 0;

    return stats;
  }

  /**
   * Attempt to find a workflow-enabled discovery feature that matches the query
   */
  private findWorkflowMatch(query: string): { workflowId: string; route?: string } | null {
    const knowledge = this.knowledgeService.getKnowledgeValue();
    if (!knowledge) {
      return null;
    }

    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
      return null;
    }

    let bestMatch: { workflowId: string; route?: string; score: number } | null = null;

    for (const route of Object.values(knowledge.routes)) {
      if (!route.discoverableFeatures || route.discoverableFeatures.length === 0) {
        continue;
      }

      const routeText = `${route.displayName} ${route.description} ${(route.keywords || []).join(' ')}`.toLowerCase();

      for (const feature of route.discoverableFeatures) {
        if (!feature.workflowId) {
          continue;
        }

        const featureText = `${feature.title} ${feature.description}`.toLowerCase();
        const haystack = `${routeText} ${featureText}`;

        let score = 0;
        for (const token of tokens) {
          if (haystack.includes(token)) {
            score += 1;
          }
        }

        if (score === 0) {
          continue;
        }

        if (!bestMatch || score > bestMatch.score) {
          bestMatch = {
            workflowId: feature.workflowId,
            route: feature.route || route.path,
            score
          };
        }
      }
    }

    return bestMatch;
  }
}
